import { useCallback, useEffect, useState } from "react";
import {
  editRequest,
  RequestItem,
  TaskListItem,
  type CheckDetailKind,
} from "../../../api/requests";
import { editItCheck, toRequestLike } from "../../../api/itCheck";

type Notify = (options: { message: string; variant: "success" | "error" | "info" }) => void;

interface UseRequestEditOptions {
  kind?: CheckDetailKind;
  data: RequestItem | null;
  setData: React.Dispatch<React.SetStateAction<RequestItem | null>>;
  taskList: TaskListItem[] | null;
  taskListLoading: boolean;
  showNotification: Notify;
  closePanels: () => void;
}

export const useRequestEdit = ({
  kind = "site",
  data,
  setData,
  taskList,
  taskListLoading,
  showNotification,
  closePanels,
}: UseRequestEditOptions) => {
  const [isEditingRequest, setIsEditingRequest] = useState(false);
  const [requestEditLoading, setRequestEditLoading] = useState(false);
  const [editCompanyName, setEditCompanyName] = useState("");
  const [editInn, setEditInn] = useState("");
  const [editUrl, setEditUrl] = useState("");

  const canEditRequest =
    data !== null &&
    !taskListLoading &&
    Array.isArray(taskList) &&
    taskList.length === 0;

  useEffect(() => {
    if (!canEditRequest && isEditingRequest) {
      setIsEditingRequest(false);
      setRequestEditLoading(false);
    }
  }, [canEditRequest, isEditingRequest]);

  const handleStartRequestEdit = useCallback(() => {
    if (!data) return;
    if (taskListLoading || !taskList || taskList.length > 0) return;
    setEditCompanyName(data.companyName);
    setEditInn(data.inn);
    setEditUrl(data.url);
    closePanels();
    setIsEditingRequest(true);
  }, [closePanels, data, taskList, taskListLoading]);

  const handleCancelRequestEdit = useCallback(() => {
    setIsEditingRequest(false);
    setRequestEditLoading(false);
  }, []);

  const handleSaveRequestEdit = useCallback(() => {
    if (!data) return;
    const companyName = editCompanyName.trim();
    const inn = editInn.trim();
    const url = editUrl.trim();
    if (kind === "it") {
      if (!companyName || !url) {
        showNotification({ message: "Заполните название продукта и URL", variant: "error" });
        return;
      }
      setRequestEditLoading(true);
      editItCheck(data.id, { productName: companyName, url })
        .then((updated) => {
          setData(toRequestLike(updated));
          setIsEditingRequest(false);
          showNotification({ message: "Заявка отредактирована", variant: "success" });
        })
        .catch(() => undefined)
        .finally(() => setRequestEditLoading(false));
      return;
    }
    if (!companyName || !inn || !url) {
      showNotification({ message: "Заполните companyName, ИНН и URL", variant: "error" });
      return;
    }
    setRequestEditLoading(true);
    editRequest(data.id, { companyName, inn, url })
      .then((updated) => {
        setData(updated);
        setIsEditingRequest(false);
        showNotification({ message: "Заявка отредактирована", variant: "success" });
      })
      .catch(() => undefined)
      .finally(() => {
        setRequestEditLoading(false);
      });
  }, [data, editCompanyName, editInn, editUrl, kind, setData, showNotification]);

  return {
    isEditingRequest,
    requestEditLoading,
    editCompanyName,
    editInn,
    editUrl,
    canEditRequest,
    setEditCompanyName,
    setEditInn,
    setEditUrl,
    handleStartRequestEdit,
    handleCancelRequestEdit,
    handleSaveRequestEdit,
  };
};
